import dayjs from 'dayjs';
import strings from '../../../../../../common/Translation/Translate';
import { identifiers } from '../../../../../../utils/constants/identifiers';

export type DropdownOption = { value: string | number; label: string };

export const toSelectValue = (value: string | number | undefined | null) =>
  value != null && value !== '' ? String(value) : '';

export type PricePackageRow = {
  id?: number;
  pricePackageId?: string | number;
  pricePackageName?: string;
  pricePerPerson?: string | number;
};

export type PriceOverrideRow = {
  id?: number;
  startDate?: string;
  endDate?: string;
  priority?: string | number;
  weekDays?: DropdownOption[];
  weekDayNames?: string;
  pricePackage?: DropdownOption;
  pricePackageId?: string | number;
  pricePerPerson?: string | number;
};

export const normalizePricePackageRow = (item: any): PricePackageRow => ({
  id: item.id,
  pricePackageId: item.pricePackageId?.value ?? item.pricePackageId,
  pricePackageName: item.pricePackageName ?? item.pricePackageId?.label,
  pricePerPerson: item.pricePerPerson ?? '',
});

export const mapPricePackagesFromApi = (items: any[] = []): PricePackageRow[] =>
  items.map(normalizePricePackageRow);

export const applyPrioritiesFromOrder = (rows: PriceOverrideRow[]): PriceOverrideRow[] =>
  rows.map((row, index) => ({ ...row, priority: index + 1 }));

export const sortPriceOverridesByPriority = (rows: PriceOverrideRow[]): PriceOverrideRow[] =>
  [...rows].sort((a, b) => Number(a.priority ?? 0) - Number(b.priority ?? 0));

export const reorderPriceOverrides = (
  rows: PriceOverrideRow[],
  fromIndex: number,
  toIndex: number,
): PriceOverrideRow[] => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= rows.length ||
    toIndex >= rows.length
  ) {
    return rows;
  }
  const next = [...rows];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return applyPrioritiesFromOrder(next);
};

export const mapPriceOverridesFromApi = (items: any[] = []): PriceOverrideRow[] =>
  applyPrioritiesFromOrder(
    sortPriceOverridesByPriority(
      items.map(item => ({
        id: item.id,
        startDate: normalizeStoredPriceDate(item.startDate ?? ''),
        endDate: normalizeStoredPriceDate(item.endDate ?? ''),
        priority: item.priority ?? '',
        weekDays: normalizeWeekDays(item.weekDays, item.weekDayNames),
        weekDayNames: item.weekDayNames,
        pricePackage: normalizePricePackageOption(item),
        pricePackageId: item.pricePackageId ?? item.pricePackage?.value,
        pricePerPerson: item.pricePerPerson ?? '',
      })),
    ),
  );

const normalizeWeekDays = (weekDays: any, weekDayNames?: string): DropdownOption[] => {
  if (Array.isArray(weekDays) && weekDays.length > 0) {
    if (typeof weekDays[0] === 'object' && weekDays[0]?.label != null) {
      return weekDays.map((d: any) => ({ value: d.value, label: d.label }));
    }
    const labels = (weekDayNames ?? '').split(',').map((s: string) => s.trim());
    return weekDays.map((id: any, i: number) => ({
      value: id,
      label: labels[i] ?? String(id),
    }));
  }
  return [];
};

const normalizePricePackageOption = (item: any): DropdownOption | undefined => {
  if (item.pricePackage?.value != null) {
    return { value: item.pricePackage.value, label: item.pricePackage.label };
  }
  if (item.pricePackageId != null && item.pricePackage) {
    return { value: item.pricePackageId, label: String(item.pricePackage) };
  }
  if (item.pricePackageId != null && item.pricePackageName) {
    return { value: item.pricePackageId, label: item.pricePackageName };
  }
  return undefined;
};

export const createEmptyPricePackageRow = (): PricePackageRow => ({
  pricePackageId: '',
  pricePerPerson: '',
});

export const createEmptyPriceOverrideRow = (): PriceOverrideRow => ({
  startDate: '',
  endDate: '',
  priority: '',
  weekDays: [],
  pricePackage: undefined,
  pricePerPerson: '',
});

/** API/storage format (DD/MM/YYYY) → DateInput wire format (YYYY-MM-DD). */
export const toDateInputValue = (ddMmYyyy: string): string => {
  if (!ddMmYyyy) return '';
  const parsed = dayjs(ddMmYyyy, identifiers.date_format, true);
  return parsed.isValid() ? parsed.format(identifiers.date_input) : '';
};

/** DateInput wire format → API/storage format. */
export const fromDateInputValue = (yyyyMmDd: string): string => {
  if (!yyyyMmDd) return '';
  const parsed = dayjs(yyyyMmDd, identifiers.date_input, true);
  return parsed.isValid() ? parsed.format(identifiers.date_format) : '';
};

const PRICE_DATE_FORMATS = [identifiers.date_format, 'DD-MM-YYYY', 'dd-MM-yyyy'] as const;

/** Parse a stored or API date string into dayjs (strict, multiple formats). */
export const parseStoredPriceDate = (value: string) => {
  if (!value) return null;
  for (const fmt of PRICE_DATE_FORMATS) {
    const parsed = dayjs(value, fmt, true);
    if (parsed.isValid()) return parsed;
  }
  return null;
};

/** Normalize API / legacy dates to the UI storage format (DD/MM/YYYY). */
export const normalizeStoredPriceDate = (value: string): string => {
  const parsed = parseStoredPriceDate(value);
  return parsed ? parsed.format(identifiers.date_format) : value;
};

/** UI or API date string → backend request format (dd-MM-yyyy). */
export const toApiPriceDate = (value: string): string => {
  const parsed = parseStoredPriceDate(value);
  return parsed ? parsed.format('DD-MM-YYYY') : '';
};

export const buildPriceOverridePayload = (item: PriceOverrideRow, index: number) => {
  const packageId = item.pricePackage?.value ?? item.pricePackageId;
  const price =
    item.pricePerPerson != null && item.pricePerPerson !== ''
      ? parseFloat(String(item.pricePerPerson))
      : undefined;

  return {
    ...(item.id != null ? { id: item.id } : {}),
    startDate: toApiPriceDate(item.startDate ?? ''),
    endDate: toApiPriceDate(item.endDate ?? ''),
    priority: Number(item.priority ?? index + 1),
    pricePackageId: packageId,
    ...(price != null && !Number.isNaN(price) ? { pricePerPerson: price } : {}),
    weekDays: extractWeekDayIds(item.weekDays),
  };
};

export const hasPriceOverridePackage = (row: PriceOverrideRow): boolean => {
  const fromOption = row.pricePackage?.value;
  if (fromOption != null && fromOption !== '') return true;
  const id = row.pricePackageId;
  return id != null && id !== '';
};

export type PriceOverrideFieldErrors = {
  pricePackage?: string;
  pricePerPerson?: string;
  startDate?: string;
  endDate?: string;
  weekDays?: string;
};

export const getPriceOverrideFieldErrors = (row: PriceOverrideRow): PriceOverrideFieldErrors => {
  const errors: PriceOverrideFieldErrors = {};

  if (!hasPriceOverridePackage(row)) {
    errors.pricePackage = strings.requiredText;
  }

  const price = String(row.pricePerPerson ?? '').trim();
  if (!price || Number.isNaN(parseFloat(price))) {
    errors.pricePerPerson = strings.requiredText;
  }

  if (!row.startDate) {
    errors.startDate = strings.requiredText;
  }
  if (!row.endDate) {
    errors.endDate = strings.requiredText;
  }

  if (!row.weekDays?.length) {
    errors.weekDays = strings.requiredText;
  }

  const start = row.startDate ? parseStoredPriceDate(row.startDate) : null;
  const end = row.endDate ? parseStoredPriceDate(row.endDate) : null;

  if (row.startDate && !start) {
    errors.startDate = strings.requiredText;
  }
  if (row.endDate && !end) {
    errors.endDate = strings.requiredText;
  }
  if (start && end) {
    if (end.isBefore(start)) {
      errors.endDate = strings.common_sameDate_error_message;
    } else if (end.isSame(start, 'day')) {
      errors.endDate = strings.editBooking_room_date_validationForSame;
    }
  }

  return errors;
};

export const priceInlineFieldSx = {
  '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' },
  minWidth: 0,
};

/** Props passed to DropDownInput (untyped JS component) for TS strict builds. */
export const priceDropDownDefaults = {
  validators: [] as never[],
  onFocus: undefined as undefined,
  inputProps: undefined as undefined,
  disabled: false,
  className: undefined as undefined,
  labelId: undefined as undefined,
  style: undefined as undefined,
  sx: undefined as undefined,
};

export type PricePackageFieldErrors = {
  pricePackageId?: string;
  pricePerPerson?: string;
};

export const hasPricePackageSelected = (row: PricePackageRow): boolean => {
  const packageId = row.pricePackageId;
  return packageId != null && packageId !== '';
};

export const getPricePackageFieldErrors = (row: PricePackageRow): PricePackageFieldErrors => {
  const errors: PricePackageFieldErrors = {};

  if (!hasPricePackageSelected(row)) {
    errors.pricePackageId = strings.requiredText;
  }

  const price = String(row.pricePerPerson ?? '').trim();
  if (!price || Number.isNaN(parseFloat(price))) {
    errors.pricePerPerson = strings.venueConfig_price_pricePerPerson_required;
  }

  return errors;
};

export const validatePricePackageRow = (row: PricePackageRow): string => {
  const fieldErrors = getPricePackageFieldErrors(row);
  return fieldErrors.pricePackageId ?? fieldErrors.pricePerPerson ?? '';
};

export const validatePriceOverrideRow = (row: PriceOverrideRow): string => {
  const fieldErrors = getPriceOverrideFieldErrors(row);
  return (
    fieldErrors.pricePackage ??
    fieldErrors.pricePerPerson ??
    fieldErrors.startDate ??
    fieldErrors.endDate ??
    fieldErrors.weekDays ??
    ''
  );
};

export const getPricePackageLabel = (row: PricePackageRow, options: DropdownOption[]): string => {
  if (row.pricePackageName) return row.pricePackageName;
  const match = options.find(o => String(o.value) === String(row.pricePackageId));
  return match?.label ?? strings.venueConfig_price_pricePackage;
};

export const getPriceOverrideTitle = (row: PriceOverrideRow): string => {
  if (row.startDate && row.endDate) {
    return `${row.startDate} – ${row.endDate}`;
  }
  return strings.venueConfig_price_titlePriceOverride;
};

export const extractWeekDayIds = (weekDays?: DropdownOption[]): Array<string | number> =>
  (weekDays ?? []).map(d => d.value);
